import type {
  AlertEvent,
  DbSnapshot,
  InstancePresence,
  ReceivedSnapshot,
  TxSummary,
} from '@apm/contracts';
import {
  AlertEvent as AlertEventSchema,
  DbSnapshot as DbSnapshotSchema,
  InstancePresence as InstancePresenceSchema,
  ReceivedSnapshot as ReceivedSnapshotSchema,
  RtRecentResult,
  TxRecentResult,
  TxSummary as TxSummarySchema,
} from '@apm/contracts';
import { shouldNotify } from '../alerts/notify';
import { request } from '../api';
import { ActivityWindow } from '../live/activity';
import { RateTrend } from '../live/trend';

/**
 * 관제 서버 SSE 하나를 받아 화면에 먹이는 스토어 (6회차 결정 2).
 *
 * 수신 즉시 그리지 않는다. 들어온 것은 큐에 쌓고 **프레임마다 한 번** 반영한다.
 * 스냅샷은 1초에 인스턴스 수만큼, 끝난 트랜잭션은 한 번에 수천 건이 올 수 있어
 * 이벤트마다 React를 깨우면 화면이 밀린다.
 *
 * DB 워처 스냅샷(9회차)도 같은 스트림의 `event: db`로 온다. 1초에 한 장이라 그대로 상태에 넣는다.
 *
 * 알림(10회차)은 `event: alert`로 온다. 발화는 목록에 넣고 복구는 뺀다. 정보성 알림(프로파일
 * 수집·폭주 억제)은 상태가 `notice`이고 `endedAt`도 채워져 와 목록에 남지 않는다 (24회차). 연결이 새로 붙을 때마다
 * 서버가 지금 발화 중인 것을 다시 보내므로, 붙는 순간 목록을 비우고 받은 것으로 다시 채운다.
 *
 * 끝난 트랜잭션 배열은 상태에 넣지 않는다. 5분에 수만 건이라 프레임마다 새 배열을 만들면
 * 그 자체가 예산을 넘는다. 대신 `txVersion`만 올리고, 캔버스는 `getRows()`로 직접 읽는다.
 *
 * 초당 요청·오류 추이(22-B회차)도 같은 태도다. 스냅샷을 `RateTrend`에 모으고 `trendVersion`만
 * 올린다. 붙을 때 `GET /api/rt/recent`로 서버가 들고 있는 30분치를 되찾아 새로고침해도 이어진다.
 *
 * 최근 1분 동시 처리 요약(23-A회차, UI 피드백 23)도 같은 자리에서 모은다. 진행 중 요청 목록은
 * 평소에 늘 비어 있어 죽어 보이는데, "가장 많을 때 몇 건이었나"를 옆에 적으면 그렇지 않다는 것을
 * 알 수 있다. 이쪽은 되찾기가 없다 — 1분치라 서버 링버퍼를 뒤질 값이 없다.
 */

/** `EventSource` 중 우리가 쓰는 부분. 테스트에서 가짜로 갈아 끼운다 */
export interface EventSourceLike {
  addEventListener(type: string, handler: (event: { data?: string }) => void): void;
  close(): void;
  /**
   * `EventSource.readyState`. 2(`CLOSED`)면 브라우저가 다시 붙지 않는다 (44회차).
   * 없으면(시험의 가짜) 닫히지 않은 것으로 본다
   */
  readonly readyState?: number;
}

/** 프레임 예약기. 반환값을 부르면 예약이 취소된다 */
export type Scheduler = (callback: () => void) => () => void;

/** 한 번 도는 타이머. 반환값을 부르면 취소된다. 재연결·끊김 감시가 쓴다 (44회차) */
export type Timer = (callback: () => void, ms: number) => () => void;

export type StreamStatus = 'idle' | 'connecting' | 'open' | 'error';

export interface LiveState {
  status: StreamStatus;
  /** 인스턴스별 최신 스냅샷. service·instance 오름차순 */
  instances: readonly ReceivedSnapshot[];
  /**
   * 보고가 끊긴 앱의 마지막 스냅샷 (19회차, 콘솔 검토 C-6).
   *
   * 16회차까지 조용해진 인스턴스를 조용히 지웠다. 화면에서는 "앱 3개 → 2개"로 숫자만 줄어
   * **어느 프로세스가 언제 끊겼는지** 알 수 없었다. 죽은 값을 게이지에 섞지는 않되,
   * 한동안 목록에 남겨 회색으로 보인다.
   */
  silent: readonly ReceivedSnapshot[];
  /** 서버가 판정한 인스턴스 생존·재시작·이름 충돌. 트레이스만 인스턴스도 들어 있다 */
  presence: readonly InstancePresence[];
  /** 마지막으로 반영한 시각. epoch ms */
  frameAt: number;
  /** 끝난 트랜잭션 목록이 바뀔 때마다 증가. 캔버스는 이 값만 보고 다시 그린다 */
  txVersion: number;
  /** 지금 들고 있는 끝난 트랜잭션 수 */
  txCount: number;
  /** 붙은 뒤 받은 누적 수 */
  txReceived: number;
  /** 대상 DB의 마지막 상태. 워처가 안 돌면 없다 (9회차 결정 4) */
  db?: DbSnapshot;
  /** 지금 발화 중인 알림. 시작이 이른 것부터 (10회차 결정 5) */
  alerts: readonly AlertEvent[];
  /** 최근 중요 알림 후보. 활성 목록에 남지 않는 장애 통보도 브라우저 알림에서 확인한다 */
  alertEvents: readonly AlertEvent[];
  /** 알림이 하나라도 오갈 때마다 증가. 이력 목록이 이 값만 보고 다시 읽는다 */
  alertVersion: number;
  /** 마지막으로 받은 알림 */
  lastAlert?: AlertEvent;
  /** 초당 요청·오류 추이에 값이 더해질 때마다 증가 (22-B회차). 그래프는 이 값만 보고 다시 계산한다 */
  trendVersion: number;
  /** 최근 1분 동시 처리 요약이 바뀔 때마다 증가 (23-A회차). 실시간 화면이 이 값만 보고 다시 센다 */
  activityVersion: number;
}

export interface StreamStoreOptions {
  /** SSE 주소. 개발 서버가 `/api`를 관제 서버로 프록시한다 */
  streamUrl?: string;
  /** 초기 로딩용 조회 주소 */
  recentUrl?: string;
  /** 추이 되찾기용 스냅샷 조회 주소. 서버 보존(기본 30분)까지만 온다 */
  recentRatesUrl?: string;
  /** X-View 가로 창. 이보다 오래된 점은 버린다 */
  windowMs?: number;
  /** 메모리 상한 */
  maxRows?: number;
  /** 이 시간 동안 스냅샷이 안 오는 인스턴스는 게이지에서 뺀다 */
  instanceTimeoutMs?: number;
  /** 끊긴 앱을 목록에 남겨 두는 시간. 이 뒤에는 아주 지운다 */
  silentKeepMs?: number;
  schedule?: Scheduler;
  open?: (url: string) => EventSourceLike;
  fetchJson?: (url: string) => Promise<unknown>;
  now?: () => number;
  /** 재연결·끊김 감시용 타이머. 시험이 갈아 끼운다 */
  timer?: Timer;
  /** 닫힌 연결을 다시 열 때 처음 기다리는 시간. 실패할 때마다 두 배, `retryMaxMs`까지 */
  retryMinMs?: number;
  retryMaxMs?: number;
  /** 이 시간 동안 아무것도 안 오면 연결이 살아 있는지 서버에 물어본다 */
  silenceMs?: number;
  /**
   * 서버가 연결을 닫았을 때 로그인 세션이 살아 있는지 (51회차). 거짓이면 다시 열지 않고 멈춘다.
   * 던지면(서버에 못 닿는다) 살아 있는 것으로 보고 지금처럼 다시 연다
   */
  checkSession?: () => Promise<boolean>;
  /**
   * 큰 초기 조회를 나눠 검증할 때 조각 사이에 한 번 쉰다 (D-C4). 그동안 브라우저가 그리고 입력을 받는다.
   * 시험이 갈아 끼운다
   */
  yieldFrame?: () => Promise<void>;
  /**
   * 단조 시계(ms). 재연결 틈과 조회에 걸린 시간을 잰다 (R62-1). 벽시계(`now`)는 사람이 시계를 맞추거나
   * 동기화로 뒤로 갈 수 있어 틈을 짧게 재면 끝난 요청을 놓친다. 시험이 갈아 끼운다
   */
  monotonic?: () => number;
}

const DEFAULT_WINDOW_MS = 5 * 60_000;
const DEFAULT_RETRY_MIN_MS = 1_000;
const DEFAULT_RETRY_MAX_MS = 30_000;
const DEFAULT_SILENCE_MS = 30_000;
/** 끊김 감시를 몇 초마다 볼지. 조용한지 확인하는 것뿐이라 싸다 */
const WATCH_MS = 5_000;
/** 끊김 감시가 묻는 곳. 최근 1분에 보고한 인스턴스가 있는지만 본다 */
const PROBE_URL = '/api/rt/recent?minutes=1';
/** `EventSource.CLOSED` */
const CLOSED = 2;
/** 계약과 달라 버린 것을 브라우저 콘솔에 몇 초마다 한 줄 남길지 */
const REJECT_WARN_MS = 60_000;
const DEFAULT_MAX_ROWS = 50_000;
const DEFAULT_INSTANCE_TIMEOUT_MS = 10_000;
const DEFAULT_SILENT_KEEP_MS = 5 * 60_000;
/** 60fps 한 프레임. rAF가 없는 곳(테스트)의 대체 주기 */
const FRAME_MS = 16;
/**
 * rAF 와 함께 거는 대체 주기 (44회차). 브라우저는 숨은 탭의 rAF 를 아예 멈추고 타이머는
 * 1초로 늦춘다. rAF 만 기다리면 숨은 동안 수신 큐가 끝없이 쌓이고 브라우저 알림도 뜨지 않는다
 */
const HIDDEN_FRAME_MS = 1_000;
/** 초기 조회를 몇 행씩 나눠 검증할지 (D-C4). 이 수 이하면 전처럼 한 번에 검증한다 */
const VALIDATE_CHUNK = 1_000;
/**
 * 다시 붙을 때 틈을 메우는 조회에 더 얹는 여유 (D-C4). 서버는 행이 피드에 들어온 시각으로 자르고
 * 화면은 마지막으로 받은 시각으로 틈을 잰다. 둘 사이의 전송 지연·타이머 늦음을 덮는다.
 * 겹쳐 받은 행은 `acceptInitial`이 버린다
 */
const GAP_MARGIN_MS = 30_000;

function defaultMonotonic(): number {
  return performance.now();
}
const TxSummaryList = TxSummarySchema.array();
/** 사건 이력은 서버가 보관한다. 화면의 알림 전달용 큐만 제한해 둔다 */
const ALERT_EVENT_LIMIT = 200;

/** 인스턴스 키. 구분자는 이름에 못 들어가는 NUL이라 'a b'+'c'와 'a'+'b c'가 겹치지 않는다 */
function instanceKey(snapshot: ReceivedSnapshot): string {
  return `${snapshot.service}\u0000${snapshot.instance}`;
}

/**
 * 프레임 하나를 예약한다. rAF 와 타이머를 함께 걸고 먼저 오는 쪽으로 한 번만 돈다 (44회차).
 * 보이는 탭은 지금처럼 rAF(16ms 안팎)가 이기고, 숨은 탭은 타이머(1초)가 이긴다
 */
export function defaultSchedule(callback: () => void): () => void {
  const hasFrames = typeof requestAnimationFrame === 'function';
  let done = false;
  let frame = 0;
  let timeout: ReturnType<typeof setTimeout> | undefined;
  const cancel = () => {
    done = true;
    if (hasFrames) cancelAnimationFrame(frame);
    if (timeout !== undefined) clearTimeout(timeout);
  };
  const run = () => {
    if (done) return;
    cancel();
    callback();
  };
  if (hasFrames) frame = requestAnimationFrame(run);
  timeout = setTimeout(run, hasFrames ? HIDDEN_FRAME_MS : FRAME_MS);
  return cancel;
}

function browserEventSource(url: string): EventSourceLike {
  const source = new EventSource(url);
  return {
    addEventListener: (type, handler) => {
      source.addEventListener(type, (event) => handler({ data: (event as MessageEvent).data }));
    },
    close: () => source.close(),
    get readyState() {
      return source.readyState;
    },
  };
}

function defaultYieldFrame(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

/** 조회 주소의 `minutes`만 바꾼다. 다른 조건은 그대로 둔다 */
function withMinutes(url: string, minutes: number): string {
  const mark = url.indexOf('?');
  const path = mark < 0 ? url : url.slice(0, mark);
  const params = new URLSearchParams(mark < 0 ? '' : url.slice(mark + 1));
  params.set('minutes', String(minutes));
  return `${path}?${params.toString()}`;
}

function defaultTimer(callback: () => void, ms: number): () => void {
  const handle = setTimeout(callback, ms);
  return () => clearTimeout(handle);
}

/** 초기 조회·끊김 감시. `request`를 거쳐 세션 401이 로그인 화면으로 이어진다 (51회차) */
async function browserFetchJson(url: string): Promise<unknown> {
  const response = await request(url);
  if (!response.ok) throw new Error(`조회 실패 ${response.status}`);
  return response.json();
}

/** 세션 확인 주소. 세션이 없으면 서버의 세션 문이 401을 준다 */
const SESSION_URL = '/api/auth/me';

async function browserCheckSession(): Promise<boolean> {
  return (await request(SESSION_URL)).status !== 401;
}

const IDLE: LiveState = {
  status: 'idle',
  instances: [],
  frameAt: 0,
  txVersion: 0,
  txCount: 0,
  txReceived: 0,
  silent: [],
  presence: [],
  alerts: [],
  alertEvents: [],
  alertVersion: 0,
  trendVersion: 0,
  activityVersion: 0,
};

type ResolvedOptions = Required<
  Pick<
    StreamStoreOptions,
    | 'streamUrl'
    | 'recentUrl'
    | 'recentRatesUrl'
    | 'windowMs'
    | 'maxRows'
    | 'instanceTimeoutMs'
    | 'silentKeepMs'
    | 'schedule'
    | 'open'
    | 'fetchJson'
    | 'now'
    | 'timer'
    | 'retryMinMs'
    | 'retryMaxMs'
    | 'silenceMs'
    | 'checkSession'
    | 'yieldFrame'
    | 'monotonic'
  >
>;

export class StreamStore {
  private readonly options: ResolvedOptions;
  private readonly listeners = new Set<() => void>();
  private readonly instances = new Map<string, ReceivedSnapshot>();
  /** 보고가 끊긴 앱의 마지막 스냅샷 (콘솔 검토 C-6) */
  private readonly silent = new Map<string, ReceivedSnapshot>();
  private readonly alerts = new Map<string, AlertEvent>();
  private pendingAlerts: AlertEvent[] = [];
  private rows: TxSummary[] = [];
  private pendingSnapshots: ReceivedSnapshot[] = [];
  /** presence 이벤트는 전체 목록이므로 마지막 한 벌만 반영한다 */
  private pendingPresence?: InstancePresence[];
  private pendingDb?: DbSnapshot;
  private pendingRows: TxSummary[] = [];
  /** 초기 로딩처럼 큐를 거치지 않고 목록을 바꾼 경우 */
  private rowsDirty = false;
  private cancelFrame?: () => void;
  private source?: EventSourceLike;
  private state: LiveState = IDLE;
  private txReceived = 0;
  /** 초당 요청·오류 추이. 연결이 끊겼다 붙어도 남는다. 로그아웃(`reset`)하면 새로 만든다 */
  private trend = new RateTrend();
  private trendDirty = false;
  /** 최근 1분 동시 처리. 진행 중 요청 목록이 비었을 때 옆에 적는 값이다 (23-A회차) */
  private activity = new ActivityWindow();
  private activityDirty = false;
  /** `start()`와 `stop()` 사이. 재연결·끊김 감시가 이것을 보고 멈춘다 (44회차) */
  private running = false;
  /** 이번 연결 묶음에서 `open`을 받은 횟수. 둘째부터는 끊긴 동안의 틈을 다시 받는다 */
  private opened = 0;
  private retryDelay: number;
  private cancelRetry?: () => void;
  private cancelWatch?: () => void;
  /** 이 연결로 마지막으로 무엇이든 받은 시각 */
  private lastEventAt = 0;
  /**
   * 끝난 요청을 빠짐없이 받았다고 볼 수 있는 마지막 시각 (D-C4). 단조 시계(`monotonic`) 값이다 (R62-1). 실시간 연결로 무엇이든 받은 시각과
   * 초기 조회가 성공한 조회의 보낸 시각 중 늦은 것이다. 연결이 바뀌어도 남는다. 다시 붙을 때 이 뒤만 받는다
   */
  private lastReceivedAt?: number;
  private lastProbeAt = 0;
  private probing = false;
  /**
   * 끊김 감시가 이번 조용한 동안 처음 물었을 때 본 인스턴스별 마지막 수신 시각.
   * 트레이스만 서비스는 스냅샷이 없어, 두 번 물어 이 값이 늘었는지로 요청이 오고 있는지 본다.
   * 서버 시각끼리만 견주므로 브라우저 시계와 무관하다. 무엇이든 받으면 비운다
   */
  private probeSeen?: Map<string, number>;
  /** `start()` 차례. 멈춘 뒤나 다음 시작 뒤에 늦게 온 초기 조회를 버린다 */
  private generation = 0;
  /** 초기 조회 상태. 붙기 전에 실패했으면 처음 붙을 때 다시 받는다 */
  private recentState: 'idle' | 'loading' | 'ok' | 'failed' = 'idle';
  private ratesState: 'idle' | 'loading' | 'ok' | 'failed' = 'idle';
  /** 계약과 달라 버린 수. 연결이 바뀌어도 누적한다 (44회차) */
  private rejected = 0;
  private lastRejectWarnAt = Number.NEGATIVE_INFINITY;

  constructor(options: StreamStoreOptions = {}) {
    this.options = {
      streamUrl: options.streamUrl ?? '/api/stream',
      recentUrl: options.recentUrl ?? '/api/tx/recent?minutes=5',
      recentRatesUrl: options.recentRatesUrl ?? '/api/rt/recent?minutes=60',
      windowMs: options.windowMs ?? DEFAULT_WINDOW_MS,
      maxRows: options.maxRows ?? DEFAULT_MAX_ROWS,
      instanceTimeoutMs: options.instanceTimeoutMs ?? DEFAULT_INSTANCE_TIMEOUT_MS,
      silentKeepMs: options.silentKeepMs ?? DEFAULT_SILENT_KEEP_MS,
      schedule: options.schedule ?? defaultSchedule,
      open: options.open ?? browserEventSource,
      fetchJson: options.fetchJson ?? browserFetchJson,
      now: options.now ?? Date.now,
      timer: options.timer ?? defaultTimer,
      retryMinMs: options.retryMinMs ?? DEFAULT_RETRY_MIN_MS,
      retryMaxMs: options.retryMaxMs ?? DEFAULT_RETRY_MAX_MS,
      silenceMs: options.silenceMs ?? DEFAULT_SILENCE_MS,
      checkSession: options.checkSession ?? browserCheckSession,
      yieldFrame: options.yieldFrame ?? defaultYieldFrame,
      monotonic: options.monotonic ?? defaultMonotonic,
    };
    this.retryDelay = this.options.retryMinMs;
  }

  // ---- useSyncExternalStore 표면 ----

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getState = (): LiveState => this.state;

  /** 캔버스가 직접 읽는다. 복사하지 않으므로 밖에서 고치지 않는다 */
  getRows = (): readonly TxSummary[] => this.rows;

  /** 초당 요청·오류 추이. 그래프가 `trendVersion`이 바뀔 때 읽는다 */
  getTrend = (): RateTrend => this.trend;

  /** 최근 1분 동시 처리. 화면이 `activityVersion`이 바뀔 때 읽는다 (23-A회차) */
  getActivity = (): ActivityWindow => this.activity;

  get windowMs(): number {
    return this.options.windowMs;
  }

  // ---- 연결 ----

  start(): void {
    if (this.running) return;
    this.running = true;
    this.generation += 1;
    this.setState({ status: 'connecting' });
    void this.loadRecent();
    void this.loadRecentRates();
    this.connect();
    this.armWatch();
  }

  /**
   * 실시간 연결 하나를 연다. 닫힌 연결을 다시 열 때도 여기로 온다 (44회차).
   * 옛 연결의 늦은 이벤트가 새 연결의 상태를 건드리지 않게, 지금 연결에서 온 것만 받는다.
   */
  private connect(): void {
    const source = this.options.open(this.options.streamUrl);
    this.source = source;
    this.lastEventAt = this.options.now();
    this.probeSeen = undefined;
    const listen = (type: string, handler: (data?: string) => void) => {
      source.addEventListener(type, (event) => {
        if (this.source !== source) return;
        if (type !== 'error') {
          this.lastEventAt = this.options.now();
          this.probeSeen = undefined;
        }
        // 서버가 보낸 것을 받았다. 이 연결로 그 앞의 끝난 요청은 다 왔다 (`open`은 아직 아무것도 안 보냈다)
        if (type !== 'error' && type !== 'open') this.lastReceivedAt = this.options.monotonic();
        handler(event.data);
      });
    };
    listen('open', () => {
      this.opened += 1;
      this.retryDelay = this.options.retryMinMs;
      // 서버가 붙는 즉시 발화 중인 것을 다시 보낸다. 옛 목록을 들고 있으면 두 벌이 된다
      this.alerts.clear();
      this.setState({ status: 'open', alerts: [] });
      // 다시 붙은 것이면 끊긴 동안의 틈을 메운다. 겹치는 행은 `acceptInitial`이 버린다.
      // 처음 붙은 것이라도 초기 조회가 실패했으면(화면을 열 때 서버가 꺼져 있었다) 지금 받는다
      // 다시 붙은 것이고 앞 조회가 성공했으면 마지막으로 받은 뒤만 받는다 (D-C4)
      if (this.opened > 1 || this.recentState === 'failed') {
        const gap = this.gapUrl();
        void this.loadRecent(gap ?? this.options.recentUrl, gap !== undefined);
      }
      if (this.opened > 1 || this.ratesState === 'failed') void this.loadRecentRates();
    });
    listen('error', () => {
      this.setState({ status: 'error' });
      // 네트워크가 끊긴 것이면 브라우저가 스스로 다시 붙는다. 오류 응답(관제 서버가 다시 뜨는
      // 동안 nginx 가 준 502 등)으로 닫힌 연결은 다시 붙지 않으므로 여기서 새로 연다.
      // 세션이 풀려 401로 닫혔을 수도 있다. `EventSource`는 상태 코드를 못 보므로 한 번 물어보고,
      // 풀렸으면 잡아 둔 재연결을 거둔다 (51회차). 44회차 그대로면 30초 간격으로 영원히 다시 열었다
      if (source.readyState === CLOSED) {
        this.reconnectLater();
        void this.confirmSession();
      }
    });
    listen('snapshot', (data) => this.onSnapshot(data));
    listen('presence', (data) => this.onPresence(data));
    listen('tx', (data) => this.onTx(data));
    listen('db', (data) => this.onDb(data));
    listen('alert', (data) => this.onAlert(data));
  }

  /** 닫힌 연결 뒤에 세션을 묻는다. 풀렸으면 멈춘다. 묻다 실패하면 재연결을 그대로 둔다 (51회차) */
  private async confirmSession(): Promise<void> {
    const generation = this.generation;
    let alive = true;
    try {
      alive = await this.options.checkSession();
    } catch {
      alive = true;
    }
    if (!alive && this.running && generation === this.generation) this.stop();
  }

  /** 지금 연결을 버리고 잠시 뒤 새로 연다. 실패가 이어지면 기다림을 두 배로 늘린다 (44회차) */
  private reconnectLater(): void {
    if (!this.running || this.cancelRetry !== undefined) return;
    this.source?.close();
    this.source = undefined;
    const delay = this.retryDelay;
    this.retryDelay = Math.min(this.retryDelay * 2, this.options.retryMaxMs);
    this.cancelRetry = this.options.timer(() => {
      this.cancelRetry = undefined;
      if (!this.running) return;
      this.setState({ status: 'connecting' });
      this.connect();
    }, delay);
  }

  /** 끊김 감시. 몇 초마다 연결이 너무 조용하지 않은지 본다 (44회차) */
  private armWatch(): void {
    this.cancelWatch = this.options.timer(() => {
      this.cancelWatch = undefined;
      if (!this.running) return;
      void this.checkSilence();
      this.armWatch();
    }, WATCH_MS);
  }

  /**
   * 열린 연결에서 한동안 아무것도 안 오면 서버에 최근 1분 보고가 있는지 묻는다 (44회차).
   *
   * 프록시(vite 개발 서버 등)는 관제 서버가 내려가도 브라우저 쪽 연결을 닫지 않을 수 있다.
   * 그러면 `error`가 오지 않아 화면이 "연결됨"인 채 멈춘다. 서버의 ping 은 주석(`: ping`)이라
   * 브라우저가 볼 수 없다. 그래서 서버는 보고를 받고 있는데 이 연결로는 아무것도 안 왔다면
   * 연결이 죽은 것으로 보고 새로 연다. 서버에 닿지 못하면 끊김으로 적고 새로 연다.
   * 보고하는 인스턴스가 하나도 없으면 조용한 것이 맞으므로 그대로 둔다.
   */
  async checkSilence(): Promise<void> {
    const now = this.options.now();
    if (this.state.status !== 'open' || this.probing) return;
    if (now - this.lastEventAt < this.options.silenceMs) return;
    if (now - this.lastProbeAt < this.options.silenceMs) return;
    this.probing = true;
    this.lastProbeAt = now;
    const source = this.source;
    // 멈춘 뒤(`stop`·`reset`) 늦게 끝난 확인이 새 연결 묶음의 상태를 건드리지 않게 한다 (리뷰 R51-B-2)
    const generation = this.generation;
    try {
      const parsed = RtRecentResult.safeParse(await this.options.fetchJson(PROBE_URL));
      // 묻는 사이에 무엇이든 왔으면 살아 있는 것이다
      if (!parsed.success || this.source !== source || this.lastEventAt > now) return;
      // 스냅샷이 오는 앱은 최근 1분 스냅샷이 있으면 곧 증거다. 트레이스만 앱은 스냅샷이 없어,
      // 이번 조용한 동안 두 번 물어 인스턴스의 마지막 수신 시각이 늘었으면(요청이 오고 있다) 증거다
      const seen = new Map(
        parsed.data.instances.map((item) => [
          `${item.service} ${item.instance ?? ''}`,
          item.lastSeenAt,
        ]),
      );
      const before = this.probeSeen;
      this.probeSeen = seen;
      const advanced =
        before !== undefined &&
        [...seen].some(([key, at]) => at > (before.get(key) ?? Number.NEGATIVE_INFINITY));
      if (parsed.data.snapshots.length > 0 || advanced) this.reconnectLater();
    } catch {
      if (this.source === source) {
        this.setState({ status: 'error' });
        this.reconnectLater();
      }
    } finally {
      if (generation === this.generation) this.probing = false;
    }
  }

  stop(): void {
    this.running = false;
    this.cancelRetry?.();
    this.cancelRetry = undefined;
    this.cancelWatch?.();
    this.cancelWatch = undefined;
    this.opened = 0;
    this.retryDelay = this.options.retryMinMs;
    this.recentState = 'idle';
    this.ratesState = 'idle';
    this.probeSeen = undefined;
    // 끝나지 않은 끊김 확인을 놓아 준다. 안 놓으면 다음 시작부터 확인이 영영 안 돈다 (리뷰 R51-B-2).
    // 늦게 끝난 그 확인은 차례(`generation`)가 달라 아무것도 건드리지 않는다
    this.probing = false;
    this.generation += 1;
    this.source?.close();
    this.source = undefined;
    this.cancelFrame?.();
    this.cancelFrame = undefined;
    this.pendingSnapshots = [];
    this.pendingPresence = undefined;
    this.pendingRows = [];
    this.pendingDb = undefined;
    this.pendingAlerts = [];
    this.alerts.clear();
    this.silent.clear();
    this.rowsDirty = false;
    this.setState({
      status: 'idle',
      alerts: [],
      alertEvents: [],
      lastAlert: undefined,
      silent: [],
      presence: [],
    });
  }

  /**
   * 연결을 닫고 받은 것을 전부 버린다 (51회차). 로그아웃·세션 만료 때 부른다.
   *
   * `stop()`은 화면을 옮겨 연결만 잠시 내리는 자리라 끝난 요청·추이·인스턴스를 남긴다. 로그아웃한
   * 뒤에 그것이 남으면 다음 사람이 로그인 화면 뒤에서 앞사람이 본 요청을 보게 된다.
   */
  reset(): void {
    this.stop();
    this.generation += 1;
    this.instances.clear();
    this.rows = [];
    this.lastReceivedAt = undefined;
    this.txReceived = 0;
    this.trend = new RateTrend();
    this.trendDirty = false;
    this.activity = new ActivityWindow();
    this.activityDirty = false;
    // `setState`는 합치므로 `db`·`lastAlert`가 남는다. 통째로 갈아 끼운다
    this.state = IDLE;
    for (const listener of this.listeners) listener();
  }

  /**
   * 화면이 붙자마자 X-View가 비어 있지 않게 최근 5분치를 한 번 받는다.
   * 봉투는 계약의 `TxRecentResult`다 (17회차). 16회차까지는 여기도 서버도 이름 없는
   * 리터럴이라 `body.rows`를 손으로 더듬었다.
   */
  private async loadRecent(
    url: string = this.options.recentUrl,
    /** 틈만 받는 조회인지 (D-C4). 창 전체 조회면 거짓이다 */
    incremental = false,
  ): Promise<void> {
    const generation = this.generation;
    const sentAt = this.options.monotonic();
    this.recentState = 'loading';
    try {
      const rows = await this.parseRecent(await this.options.fetchJson(url));
      // 멈춘 뒤나 다음 시작 뒤에 늦게 온 답은 버린다 (44회차)
      if (!this.running || generation !== this.generation) return;
      this.recentState = rows !== undefined ? 'ok' : 'failed';
      if (rows === undefined) return;
      this.acceptInitial(rows);
      /*
       * 서버는 **조회를 처리한 시각**에서 `minutes`를 거슬러 자른다. 보낸 뒤 서버가 처리하기까지 늦으면
       * 그만큼 앞쪽이 잘린다. 여유(30초)는 그 늦음을 덮으려는 것이라, 보내고 받기까지 여유보다 오래
       * 걸린 틈 조회는 구멍이 있을 수 있다. 받은 행은 넣되 확정하지 않고 창 전체를 다시 받는다 (R62-1)
       */
      if (incremental && this.options.monotonic() - sentAt > GAP_MARGIN_MS) {
        void this.loadRecent();
        return;
      }
      // 보낸 순간 서버 피드에 있던 행은 이 답에 다 들었다
      this.lastReceivedAt = Math.max(this.lastReceivedAt ?? sentAt, sentAt);
    } catch {
      // 초기 로딩 실패는 화면을 막지 않는다. 처음 붙을 때 다시 받고, 그 뒤는 tx 이벤트로 채워진다
      if (this.running && generation === this.generation) this.recentState = 'failed';
    }
  }

  /**
   * 다시 붙을 때 틈을 메울 조회 주소 (D-C4). 서버에 `since`가 없어 `minutes`를 마지막으로 받은 시각
   * 기준으로 줄인다. 앞 초기 조회가 성공하지 않았거나(가진 행에 구멍이 있을 수 있다) 받은 적이 없거나
   * 틈이 창보다 길면(또는 틈을 믿을 수 없으면) `undefined`이고 전처럼 창 전체를 받는다
   */
  private gapUrl(): string | undefined {
    if (this.recentState !== 'ok' || this.lastReceivedAt === undefined) return undefined;
    // 틈은 단조 시계로 잰다. 벽시계가 뒤로 가면 틈이 짧게 재어져 행을 놓친다 (R62-1)
    const gap = this.options.monotonic() - this.lastReceivedAt;
    // 음수·숫자 아님은 시계를 믿을 수 없다는 뜻이다. 누락 없는 쪽(창 전체)으로 간다
    if (!Number.isFinite(gap) || gap < 0) return undefined;
    // 0.01분(0.6초) 단위로 올린다. 서버는 소수 분을 받는다
    const minutes = Math.ceil(((gap + GAP_MARGIN_MS) / 60_000) * 100) / 100;
    if (minutes >= this.options.windowMs / 60_000) return undefined;
    return withMinutes(this.options.recentUrl, minutes);
  }

  /**
   * 초기 조회 응답을 계약으로 거른다. 결과는 `TxRecentResult.safeParse` 한 번과 같다 — 한 행이라도
   * 틀리면 통째로 버린다 (D-C4). 다만 행이 많으면 1,000행씩 나눠 조각 사이에 쉰다. 5만 행을 한 번에
   * 검증하면 그동안 화면이 멈춘다
   */
  private async parseRecent(body: unknown): Promise<TxSummary[] | undefined> {
    const rows =
      typeof body === 'object' && body !== null ? (body as { rows?: unknown }).rows : undefined;
    if (!Array.isArray(rows) || rows.length <= VALIDATE_CHUNK) {
      const parsed = TxRecentResult.safeParse(body);
      return parsed.success ? parsed.data.rows : undefined;
    }
    // 봉투(분·건수)는 행을 빼고 한 번에 본다
    if (!TxRecentResult.safeParse({ ...(body as object), rows: [] }).success) return undefined;
    const out: TxSummary[] = [];
    for (let start = 0; start < rows.length; start += VALIDATE_CHUNK) {
      if (start > 0) await this.options.yieldFrame();
      const part = TxSummaryList.safeParse(rows.slice(start, start + VALIDATE_CHUNK));
      if (!part.success) return undefined;
      for (const row of part.data) out.push(row);
    }
    return out;
  }

  /**
   * 서버 링버퍼의 스냅샷으로 추이를 되찾는다 (22-B회차). 실시간으로 이미 받은 초와 겹치면
   * `RateTrend`가 한 번만 센다. 실패해도 화면을 막지 않는다
   */
  private async loadRecentRates(): Promise<void> {
    const generation = this.generation;
    this.ratesState = 'loading';
    try {
      const parsed = RtRecentResult.safeParse(
        await this.options.fetchJson(this.options.recentRatesUrl),
      );
      if (!this.running || generation !== this.generation) return;
      this.ratesState = parsed.success ? 'ok' : 'failed';
      if (parsed.success) {
        this.acceptRates(parsed.data.snapshots);
        this.acceptPresence(parsed.data.instances);
      }
    } catch {
      // 되찾기 실패는 그 앞이 비어 보일 뿐이다. 처음 붙을 때 한 번 더 받는다
      if (this.running && generation === this.generation) this.ratesState = 'failed';
    }
  }

  /** 되찾은 스냅샷 묶음. 테스트가 직접 부른다 */
  acceptRates(snapshots: readonly ReceivedSnapshot[]): void {
    const now = this.options.now();
    let added = false;
    for (const snapshot of snapshots) if (this.trend.add(snapshot, now)) added = true;
    if (!added) return;
    this.trendDirty = true;
    this.requestFrame();
  }

  /**
   * 초기 로딩분. `acceptTx`와 달리 **이미 가진 것과 겹치는 행을 버린다**.
   * 재연결(개발 모드의 StrictMode 재장착 포함)로 다시 받으면 같은 행이 두 벌 쌓인다.
   * 조회 창은 지금까지 SSE로 받은 것보다 앞선 구간이므로 목록 앞에 붙인다.
   */
  acceptInitial(loaded: readonly TxSummary[]): void {
    if (loaded.length === 0) return;
    const known = new Set<string>();
    for (const row of this.rows) known.add(row.txId);
    for (const row of this.pendingRows) known.add(row.txId);
    const fresh = loaded.filter((row) => !known.has(row.txId));
    if (fresh.length === 0) return;
    this.rows = fresh.concat(this.rows);
    // 다시 붙어서 받은 틈은 이미 가진 행보다 새것일 수 있다. 창 자르기(`trimRows`)가 앞에서부터
    // 보므로 끝난 시각 순으로 세운다. 같으면 원래 순서를 지킨다(안정 정렬) (44회차)
    this.rows.sort((a, b) => a.startedAt + a.durationMs - (b.startedAt + b.durationMs));
    this.txReceived += fresh.length;
    this.rowsDirty = true;
    this.requestFrame();
  }

  // ---- 수신 ----
  //
  // 실시간 연결로 온 것도 계약으로 거른다 (44회차). 43회차까지는 생존 목록만 검증하고 나머지는
  // 캐스팅만 해서, 판이 어긋난 서버의 보고 한 장이 게이지·동시 처리 집계에서 던져 실시간 화면을
  // 죽였다. 계약은 모르는 칸을 버리므로(`z.object`) 새 서버가 칸을 더해도 걸리지 않고, 옛 에이전트
  // 보고의 `slow` 버킷 같은 기본값도 여기서 채워진다. 끝난 요청은 한 행씩 거르고 틀린 행만 버린다.

  private onSnapshot(data?: string): void {
    const parsed = ReceivedSnapshotSchema.safeParse(parseJson<unknown>(data));
    if (parsed.success) this.acceptSnapshot(parsed.data);
    else this.noteRejected('실시간 보고');
  }

  private onPresence(data?: string): void {
    const parsed = InstancePresenceSchema.array().safeParse(parseJson<unknown>(data));
    if (parsed.success) this.acceptPresence(parsed.data);
    else this.noteRejected('인스턴스 생존 목록');
  }

  private onTx(data?: string): void {
    const parsed = parseJson<unknown>(data);
    if (!Array.isArray(parsed)) {
      this.noteRejected('끝난 요청 묶음');
      return;
    }
    const rows: TxSummary[] = [];
    let bad = 0;
    for (const item of parsed) {
      const row = TxSummarySchema.safeParse(item);
      if (row.success) rows.push(row.data);
      else bad += 1;
    }
    if (bad > 0) this.noteRejected('끝난 요청', bad);
    this.acceptTx(rows);
  }

  private onDb(data?: string): void {
    const parsed = DbSnapshotSchema.safeParse(parseJson<unknown>(data));
    if (parsed.success) this.acceptDb(parsed.data);
    else this.noteRejected('DB 상태');
  }

  private onAlert(data?: string): void {
    const parsed = AlertEventSchema.safeParse(parseJson<unknown>(data));
    if (parsed.success) this.acceptAlert(parsed.data);
    else this.noteRejected('알림');
  }

  /** 계약과 달라 버린 수. 시험과 진단이 읽는다 */
  getRejected = (): number => this.rejected;

  /**
   * 버린 것을 센다. 관제 담당자는 개발자 도구를 열지 않으므로 화면에 알릴지는 결정 항목(D44-2)이고,
   * 지금은 브라우저 콘솔에 1분에 한 줄만 남긴다
   */
  private noteRejected(what: string, count = 1): void {
    this.rejected += count;
    const now = this.options.now();
    if (now - this.lastRejectWarnAt < REJECT_WARN_MS) return;
    this.lastRejectWarnAt = now;
    console.warn(
      `실시간 연결로 온 ${what}이(가) 계약과 달라 버렸다 (누적 ${this.rejected}건). ` +
        '관제 화면과 관제 서버를 같은 판으로 맞췄는지 확인할 것',
    );
  }

  /** 테스트·초기 로딩이 직접 부른다 */
  acceptSnapshot(snapshot: ReceivedSnapshot): void {
    this.pendingSnapshots.push(snapshot);
    this.requestFrame();
  }

  /** 서버가 보낸 전체 생존 목록. 빈 배열도 기존 목록을 지우는 뜻이다 */
  acceptPresence(presence: readonly InstancePresence[]): void {
    this.pendingPresence = [...presence];
    this.requestFrame();
  }

  /** DB 스냅샷은 1초에 한 장이라 마지막 것만 들고 있으면 된다 */
  acceptDb(snapshot: DbSnapshot): void {
    this.pendingDb = snapshot;
    this.requestFrame();
  }

  /** 알림 한 건. 발화는 목록에 넣고, 복구·정보성은 뺀다 */
  acceptAlert(event: AlertEvent): void {
    this.pendingAlerts.push(event);
    this.requestFrame();
  }

  acceptTx(rows: readonly TxSummary[]): void {
    if (rows.length === 0) return;
    // 스프레드로 한 번에 넣으면 수만 건에서 인자 상한에 걸린다
    for (const row of rows) this.pendingRows.push(row);
    // 반영이 늦어져도 큐가 창 상한을 넘게 쌓이지 않게 한다. 넘친 앞쪽은 반영돼도 곧 잘릴 행이다 (44회차)
    const over = this.pendingRows.length - this.options.maxRows;
    if (over > 0) this.pendingRows.splice(0, over);
    this.requestFrame();
  }

  // ---- 프레임 ----

  private requestFrame(): void {
    if (this.cancelFrame) return;
    this.cancelFrame = this.options.schedule(() => {
      this.cancelFrame = undefined;
      this.flush();
    });
  }

  /** 프레임 하나. 큐에 쌓인 것을 한 번에 반영하고 구독자를 한 번만 깨운다 */
  flush(): void {
    const now = this.options.now();
    let instancesChanged = false;
    let rowsChanged = this.rowsDirty;
    this.rowsDirty = false;
    const presenceChanged = this.pendingPresence !== undefined;
    const presence = presenceChanged
      ? sortPresence(this.pendingPresence ?? [])
      : this.state.presence;
    this.pendingPresence = undefined;

    let trendChanged = this.trendDirty;
    this.trendDirty = false;
    let activityChanged = this.activityDirty;
    this.activityDirty = false;
    if (this.pendingSnapshots.length > 0) {
      for (const snapshot of this.pendingSnapshots) {
        const key = instanceKey(snapshot);
        // 다시 보고를 시작하면 끊김 목록에서 뺀다
        this.silent.delete(key);
        this.instances.set(key, snapshot);
        if (this.trend.add(snapshot, now)) trendChanged = true;
        if (this.activity.add(snapshot, now)) activityChanged = true;
      }
      this.pendingSnapshots = [];
      instancesChanged = true;
    }
    if (this.pruneInstances(now)) instancesChanged = true;

    if (this.pendingRows.length > 0) {
      for (const row of this.pendingRows) this.rows.push(row);
      this.txReceived += this.pendingRows.length;
      this.pendingRows = [];
      rowsChanged = true;
    }
    if (this.trimRows(now)) rowsChanged = true;

    // 워처가 멈추면 마지막 상태가 계속 떠 있는다. 인스턴스 게이지와 같은 기준으로 걷어낸다
    let db = this.state.db;
    let dbChanged = false;
    if (this.pendingDb !== undefined) {
      db = this.pendingDb;
      this.pendingDb = undefined;
      dbChanged = true;
    }
    if (db !== undefined && now - db.ts > this.options.instanceTimeoutMs) {
      db = undefined;
      dbChanged = true;
    }

    let alertsChanged = false;
    let lastAlert = this.state.lastAlert;
    let alertEvents = this.state.alertEvents;
    if (this.pendingAlerts.length > 0) {
      // 정보성 사건이 몰려도 중요 장애를 표시 전에 밀어내지 않도록 후보만 보관한다.
      const candidates = this.pendingAlerts.filter((event) => shouldNotify(event, true));
      if (candidates.length > 0) {
        alertEvents = [...alertEvents, ...candidates].slice(-ALERT_EVENT_LIMIT);
      }
      for (const event of this.pendingAlerts) {
        // 해제·통보는 경보 목록에서 뺀다. 통보(`notice`)는 애초에 들어가지도 않는다
        if (event.state !== 'firing' || event.endedAt !== undefined) this.alerts.delete(event.id);
        else this.alerts.set(event.id, event);
        lastAlert = event;
      }
      this.pendingAlerts = [];
      alertsChanged = true;
    }

    if (
      !instancesChanged &&
      !presenceChanged &&
      !rowsChanged &&
      !dbChanged &&
      !alertsChanged &&
      !trendChanged &&
      !activityChanged
    ) {
      return;
    }

    this.setState({
      instances: instancesChanged ? sortInstances(this.instances) : this.state.instances,
      silent: instancesChanged ? sortInstances(this.silent) : this.state.silent,
      presence,
      frameAt: now,
      txVersion: rowsChanged ? this.state.txVersion + 1 : this.state.txVersion,
      txCount: this.rows.length,
      txReceived: this.txReceived,
      db,
      alerts: alertsChanged ? sortAlerts(this.alerts) : this.state.alerts,
      alertEvents,
      alertVersion: alertsChanged ? this.state.alertVersion + 1 : this.state.alertVersion,
      lastAlert,
      trendVersion: trendChanged ? this.state.trendVersion + 1 : this.state.trendVersion,
      activityVersion: activityChanged
        ? this.state.activityVersion + 1
        : this.state.activityVersion,
    });
  }

  /**
   * 조용해진 인스턴스는 게이지에서 뺀다. 죽은 프로세스의 마지막 값이 계속 떠 있으면 오해를 낳는다.
   * 다만 아주 지우지는 않고 끊김 목록으로 옮긴다 (콘솔 검토 C-6). 화면이 "언제부터 안 온다"를
   * 말할 수 있어야 한다. 한참 뒤에는 그 목록에서도 뺀다.
   */
  private pruneInstances(now: number): boolean {
    let removed = false;
    for (const [key, snapshot] of this.instances) {
      if (now - snapshot.ts <= this.options.instanceTimeoutMs) continue;
      this.instances.delete(key);
      this.silent.set(key, snapshot);
      removed = true;
    }
    for (const [key, snapshot] of this.silent) {
      if (now - snapshot.ts <= this.options.silentKeepMs) continue;
      this.silent.delete(key);
      removed = true;
    }
    return removed;
  }

  /** 창 밖으로 나간 점과 상한 초과분을 앞에서부터 버린다 */
  private trimRows(now: number): boolean {
    const cutoff = now - this.options.windowMs;
    let drop = 0;
    while (drop < this.rows.length) {
      const row = this.rows[drop] as TxSummary;
      if (row.startedAt + row.durationMs >= cutoff) break;
      drop += 1;
    }
    const over = this.rows.length - drop - this.options.maxRows;
    if (over > 0) drop += over;
    if (drop === 0) return false;
    this.rows.splice(0, drop);
    return true;
  }

  private setState(patch: Partial<LiveState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }
}

function sortInstances(instances: ReadonlyMap<string, ReceivedSnapshot>): ReceivedSnapshot[] {
  return [...instances.values()].sort((a, b) =>
    a.service === b.service
      ? a.instance.localeCompare(b.instance)
      : a.service.localeCompare(b.service),
  );
}

function sortPresence(rows: readonly InstancePresence[]): InstancePresence[] {
  return [...rows].sort((a, b) =>
    a.service === b.service
      ? (a.instance ?? '').localeCompare(b.instance ?? '')
      : a.service.localeCompare(b.service),
  );
}

/** 오래 발화한 것이 위로. 화면의 카드 순서가 흔들리지 않게 시작 시각으로만 정렬한다 */
function sortAlerts(alerts: ReadonlyMap<string, AlertEvent>): AlertEvent[] {
  return [...alerts.values()].sort((a, b) => a.startedAt - b.startedAt);
}

function parseJson<T>(data?: string): T | undefined {
  if (data === undefined || data === '') return undefined;
  try {
    return JSON.parse(data) as T;
  } catch {
    return undefined;
  }
}

/** 화면 전체가 공유하는 스토어 하나 */
export const streamStore = new StreamStore();
